// ===== Packages =====

import React, {
    useRef,
    useMemo,
    useState,
    useEffect,
}                                       from 'react';
import {
    useNavigate,
    useLocation,
}                                       from 'react-router-dom';
import { ReactSVG }                     from 'react-svg';

// ===== Enums =====

import {
    CHECKOUT_CHECKPOINT,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    PAGE_ROUTE,
    STORE_ITEM_TYPE,
    USER_ACTION_TYPE,
}                                       from '../../enums';

// ===== Services =====

import {
    recordUserAction,
    updatePageTitle,
    detectTouchDevice,
    playAudio,
    stripeGetStoreItems,
    roundToNDecimals,
    updateUser,
    isTestMode,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
} from '../../services';
import buildStoreItemsByProduct            from '../../services/buildStoreItemsByProduct';
import getCartBookInviteNote                from '../../services/getCartBookInviteNote';
import useLazyAudioRef                     from '../../services/useLazyAudioRef';

// ===== Interfaces =====

import {
    ICartItem,
    IPartialUserItem,
    IStoreItem,
    ISnackbarItem,
}                                       from '../../interfaces';

// ===== Images =====

import VerascopeLogo                    from '../../images/verascope-logo-silhouette.svg';
import PixelCartIcon                    from '../../images/8-bit_cart.svg';
import PixelCrossIcon                   from '../../images/8-bit_cross.svg';
import PixelPlusIcon                    from '../../images/8-bit_plus.svg';
import PixelMinusIcon                   from '../../images/8-bit_minus.svg';
import BoxIcon                          from '../../images/box.svg';
import Spinner                          from '../../images/editor/spinner.svg';

// ===== Sounds =====

import Success                          from '../../sounds/message_success.mp3';
import Delete                           from '../../sounds/delete.mp3';

// ===== Constants =====

import {
    HOVER_TARGET_CLASSNAME,
    DEFAULT_AUDIO_VOLUME,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    CENTS_IN_A_DOLLAR,
    WEB_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_DAY_PASS_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_DAY_PASS_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_WEEK_PASS_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_WEEK_PASS_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_TEST_API_ID,
    WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_LIVE_API_ID,
    WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_TEST_API_ID,
    DIGITAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    DIGITAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    AUDIO_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    AUDIO_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    PHYSICAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
    PHYSICAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
    BODY_FONT_SIZE,
    SUPPORT_EMAIL_ADDRESS,
    DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
    DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
}                                       from '../../constants/generalConstants';
import CURSOR_SIGN                      from '../../constants/cursorSigns';

// ===== Styles =====

import {
    Container,
    ContentContainer,
    PopulatedCart,
    EmptyCart,
    EmptyCartIcon,
    EmptyCartTitle,
    CartItemsContainer,
    OrderSummaryContainer,
    Header,
    HeaderText,
    CartItemsCountText,
    EmptyCartButtonContainer,
    EmptyCartButton,
    CartItems,
    CartItemContainer,
    CartItemImageContainer,
    CartItemDetails,
    CartItemName,
    CartItemNote,
    CartItemRemoveButton,
    CartItemQuantityContainer,
    CartItemQuantityText,
    CartItemQuantityButton,
    CartItemQuantityCount,
    CartItemQuantityPriceContainer,
    OrderSummary,
    OrderSummaryEntries,
    OrderSummaryEntry,
    OrderSummaryText,
    OrderSummaryPrice,
    CheckoutButton,
    CheckoutTrustContainer,
    CheckoutTrustText,
    CheckoutTrustLinkRow,
    CheckoutTrustLink,
    BuyBookButton,
    CartItemPrice,
    CartItemImage,
    DiscountedCartItemPrice,
    CartItemPriceAfterDiscount,
    SpinnerContainer,
}                                       from './styles';
import {
    PageLogo,
    PageSubtitle,
    PageTitle,
    PlaceholderBox,
}                                       from '../../styles';
import { theme }                        from '../../themes/theme-context';

interface Props {
    customCursorActive: boolean,
    user: IPartialUserItem | null,
    userCartItems: ICartItem[] | undefined,
    hasSound: boolean,
    currentSessionId: string | null,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    setQueueRevealBuyButtons: React.Dispatch<React.SetStateAction<boolean>>,
    setUserCartItems: React.Dispatch<React.SetStateAction<ICartItem[] | undefined>>,
}
function CartView({
    customCursorActive,
    user,
    userCartItems,
    hasSound,
    currentSessionId,
    setSnackbarData,
    onCursorEnter,
    onCursorLeave,
    setQueueRevealBuyButtons,
    setUserCartItems,
}: Props): JSX.Element {
    // ===== General Constants =====
    const useStripeTestMode = isTestMode();

    // Snackbar Message when encountering error removing cart item
    const SNACKBAR_MESSAGE_REMOVE_CART_ITEM_ERROR = 'Encountered an error removing cart item. Please try again.';
    // Snackbar Message when encountering error incrementing cart item
    const SNACKBAR_MESSAGE_INCREMENT_CART_ITEM_ERROR = 'Encountered an error incrementing cart item quantity. Please try again.';
    // Snackbar Message when encountering error decrementing cart item
    const SNACKBAR_MESSAGE_DECREMENT_CART_ITEM_ERROR = 'Encountered an error decrementing cart item quantity. Please try again.';
    const CART_ITEM_NAME_PLACEHOLDER_WIDTH = 18.75 * BODY_FONT_SIZE;
    const CART_ITEM_NAME_PLACEHOLDER_HEIGHT = 1.25 * BODY_FONT_SIZE;
    const CART_ITEM_PRICE_PLACEHOLDER_WIDTH = 4.375 * BODY_FONT_SIZE;
    const CART_ITEM_PRICE_PLACEHOLDER_HEIGHT = 1.25 * BODY_FONT_SIZE;
    const ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH = 5 * BODY_FONT_SIZE;
    const ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT = 1.25 * BODY_FONT_SIZE;
    const CART_UPDATE_DEBOUNCE_DURATION = 200;

    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();

    // ===== Refs =====

    // ----- Sound Clips
    const successClip = useLazyAudioRef({
        src: Success,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const deleteClip = useLazyAudioRef({
        src: Delete,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    // Debounce timer for cart writes so bursty +/- clicks coalesce into one server update.
    const pendingCartSyncTimeoutRef = useRef<number | null>(null);
    // Last locally computed cart payload queued for server sync.
    const pendingCartSyncPayloadRef = useRef<ICartItem[] | undefined>(undefined);

    // ===== State =====

    // Stores the location key for which a view action has been recorded.
    const recordedViewPageUserActionKey = useRef<string | null>(null);
    // Stores a list of cart items
    const [storeItemsByPrice, setStoreItemsByPrice] = useState<Map<string, IStoreItem>>(new Map());
    // Indicates whether store items have been fetched
    const [fetchedStoreItems, setFetchedStoreItems] = useState<boolean>(false);
    // Stores store item information of web book
    const [webBookStoreItem, setWebBookStoreItem] = useState<IStoreItem | undefined>(undefined);

    // ===== Methods =====

    const onLogoEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.logomark,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onLogoLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onLogoClick = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.clickHomeButton,
                sessionId: currentSessionId,
                payload: {
                    route: PAGE_ROUTE.cart,
                },
            });
        }
        navigate(
            `/${PAGE_ROUTE.landing}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    /**
     * Records user action of viewing cart page
     */
    const recordCartPageUserAction = (): void => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.viewCartPage,
                sessionId: currentSessionId,
            });
        }
    };

    /**
     * Fetch stripe product prices
     */
    const fetchStoreItems = (): void => {
        const negotiationRequestPriceIds: string[] = userCartItems
            ? userCartItems.filter((item: ICartItem) => item.saleNegotiationPriceId != null)
                .map((item: ICartItem) => item.saleNegotiationPriceId!)
            : [];
        stripeGetStoreItems({
            negotiationRequestPriceIds,
            test: useStripeTestMode,
        }).then((itemsByPrice) => {
            if (itemsByPrice) {
                setStoreItemsByPrice(itemsByPrice);
                const itemsByProduct = buildStoreItemsByProduct({
                    itemsByPrice,
                    negotiationRequestPriceIds,
                });
                setWebBookStoreItem(itemsByProduct.get(WEB_BOOK_PRODUCT_ID));
                setFetchedStoreItems(true);
            }
        });
    };

    const onBuyBookButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.cartBuyBookButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onBuyBookButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onBuyBookButtonClick = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        setQueueRevealBuyButtons(true);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.viewReadPage,
                sessionId: currentSessionId,
                payload: {
                    route: PAGE_ROUTE.cart,
                },
            });
        }
        navigate(
            `/${PAGE_ROUTE.book}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    const onEmptyCartButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.clearCartButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onEmptyCartButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onEmptyCartButtonClick = (): void => {
        // Update locally
        setUserCartItems([]);
        // Update on server (debounced so rapid cart edits coalesce cleanly)
        queueCartSync([]);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.emptyCart,
                sessionId: currentSessionId,
            });
        }
        // Play Sound
        if (hasSound && deleteClip.current) {
            deleteClip.current.pause();
            deleteClip.current.currentTime = 0;
            playAudio(deleteClip.current);
        }
    };

    const onCartItemQuantityButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.cartItemQuantityButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onCartItemQuantityButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onCartItemRemoveButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.cartItemRemoveButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onCartItemRemoveButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const queueCartSync = (nextCart: ICartItem[]): void => {
        // Keep UI optimistic and responsive, then batch sync after a short pause.
        pendingCartSyncPayloadRef.current = nextCart.length > 0
            ? [...nextCart]
            : undefined;
        if (pendingCartSyncTimeoutRef.current != null) {
            window.clearTimeout(pendingCartSyncTimeoutRef.current);
            pendingCartSyncTimeoutRef.current = null;
        }
        pendingCartSyncTimeoutRef.current = window.setTimeout(() => {
            const payload = pendingCartSyncPayloadRef.current;
            updateUser({
                test: useStripeTestMode,
                cart: payload,
                cartToEmpty: !payload || payload.length === 0,
            });
            pendingCartSyncTimeoutRef.current = null;
        }, CART_UPDATE_DEBOUNCE_DURATION);
    };

    useEffect(() => () => {
        if (pendingCartSyncTimeoutRef.current != null) {
            window.clearTimeout(pendingCartSyncTimeoutRef.current);
            pendingCartSyncTimeoutRef.current = null;
        }
    }, []);

    const onCartItemIncrementQuantityButtonClick = (productId: string): void => {
        if (!isQuantityAdjustableProductId(productId)) return;

        if (userCartItems) {
            const newCart: ICartItem[] = [];
            userCartItems.forEach((item: ICartItem) => {
                if (item.productId === productId) {
                    newCart.push({
                        ...item,
                        quantity: item.quantity + 1,
                        updated: Date.now(),
                    });
                } else {
                    newCart.push(item);
                }
            });
            // Update locally
            setUserCartItems(newCart);
            // Update in server (debounced so rapid cart edits coalesce cleanly)
            queueCartSync(newCart);
            if (currentSessionId) {
                let type: STORE_ITEM_TYPE | undefined;
                switch (productId) {
                case WEB_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBook;
                    break;
                case WEB_BOOK_DAY_PASS_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookDayPass;
                    break;
                case WEB_BOOK_WEEK_PASS_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookWeekPass;
                    break;
                case WEB_BOOK_CHAPTER_ONE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterOne;
                    break;
                case WEB_BOOK_CHAPTER_TWO_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTwo;
                    break;
                case WEB_BOOK_CHAPTER_THREE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterThree;
                    break;
                case WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFour;
                    break;
                case WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFive;
                    break;
                case WEB_BOOK_CHAPTER_SIX_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSix;
                    break;
                case WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSeven;
                    break;
                case WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEight;
                    break;
                case WEB_BOOK_CHAPTER_NINE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterNine;
                    break;
                case WEB_BOOK_CHAPTER_TEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTen;
                    break;
                case WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEleven;
                    break;
                case WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTwelve;
                    break;
                case WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterThirteen;
                    break;
                case WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFourteen;
                    break;
                case WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFifteen;
                    break;
                case WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSixteen;
                    break;
                case WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSeventeen;
                    break;
                case WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEighteen;
                    break;
                case WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterNineteen;
                    break;
                case WEB_BOOK_APPENDIX_A_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookAppendixA;
                    break;
                case DIGITAL_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.digitalBook;
                    break;
                case DIGITAL_BOOK_PREORDER_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.digitalBookPreorder;
                    break;
                case AUDIO_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.audioBook;
                    break;
                case PHYSICAL_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.physicalBook;
                    break;
                case PHYSICAL_BOOK_PREORDER_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.physicalBookPreorder;
                    break;
                default:
                    // Shouldn't reach here
                }
                // Record user action
                if (type) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.incrementItemQuantity,
                        sessionId: currentSessionId,
                        payload: {
                            type,
                        },
                    });
                } else {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.incrementItemQuantity,
                        sessionId: currentSessionId,
                    });
                }
            }
            // Play Sound
            if (hasSound && successClip.current) {
                successClip.current.pause();
                successClip.current.currentTime = 0;
                playAudio(successClip.current);
            }
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_INCREMENT_CART_ITEM_ERROR,
                icon: PixelCartIcon,
                hasFailure: true,
            });
        }
    };

    const onCartItemDecrementQuantityButtonClick = (productId: string): void => {
        if (!isQuantityAdjustableProductId(productId)) return;

        if (userCartItems) {
            const newCart: ICartItem[] = [];
            let removeItem = false;
            userCartItems.forEach((item: ICartItem) => {
                if (
                    item.productId === productId
                    && item.quantity > 1
                ) {
                    newCart.push({
                        ...item,
                        quantity: item.quantity - 1,
                        updated: Date.now(),
                    });
                } else if (
                    item.productId === productId
                    && item.quantity === 1
                ) {
                    // Do noting to remove from cart
                    removeItem = true;
                } else {
                    newCart.push(item);
                }
            });
            // Update locally
            setUserCartItems(newCart);
            // Update in server (debounced so rapid cart edits coalesce cleanly)
            queueCartSync(newCart);
            if (currentSessionId) {
                let type: STORE_ITEM_TYPE | undefined;
                switch (productId) {
                case WEB_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBook;
                    break;
                case WEB_BOOK_DAY_PASS_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookDayPass;
                    break;
                case WEB_BOOK_WEEK_PASS_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookWeekPass;
                    break;
                case WEB_BOOK_CHAPTER_ONE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterOne;
                    break;
                case WEB_BOOK_CHAPTER_TWO_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTwo;
                    break;
                case WEB_BOOK_CHAPTER_THREE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterThree;
                    break;
                case WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFour;
                    break;
                case WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFive;
                    break;
                case WEB_BOOK_CHAPTER_SIX_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSix;
                    break;
                case WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSeven;
                    break;
                case WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEight;
                    break;
                case WEB_BOOK_CHAPTER_NINE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterNine;
                    break;
                case WEB_BOOK_CHAPTER_TEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTen;
                    break;
                case WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEleven;
                    break;
                case WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTwelve;
                    break;
                case WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterThirteen;
                    break;
                case WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFourteen;
                    break;
                case WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFifteen;
                    break;
                case WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSixteen;
                    break;
                case WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSeventeen;
                    break;
                case WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEighteen;
                    break;
                case WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterNineteen;
                    break;
                case WEB_BOOK_APPENDIX_A_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookAppendixA;
                    break;
                case DIGITAL_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.digitalBook;
                    break;
                case DIGITAL_BOOK_PREORDER_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.digitalBookPreorder;
                    break;
                case AUDIO_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.audioBook;
                    break;
                case PHYSICAL_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.physicalBook;
                    break;
                case PHYSICAL_BOOK_PREORDER_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.physicalBookPreorder;
                    break;
                default:
                    // Shouldn't reach here
                }
                // Record user action
                if (type) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: removeItem
                            ? USER_ACTION_TYPE.removeItemFromCart
                            : USER_ACTION_TYPE.decrementItemQuantity,
                        sessionId: currentSessionId,
                        payload: {
                            type,
                        },
                    });
                } else {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: removeItem
                            ? USER_ACTION_TYPE.removeItemFromCart
                            : USER_ACTION_TYPE.decrementItemQuantity,
                        sessionId: currentSessionId,
                    });
                }
            }
            // Play Sound
            if (hasSound && deleteClip.current) {
                deleteClip.current.pause();
                deleteClip.current.currentTime = 0;
                playAudio(deleteClip.current);
            }
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_DECREMENT_CART_ITEM_ERROR,
                icon: PixelCartIcon,
                hasFailure: true,
            });
        }
    };

    const onCartItemRemoveButtonClick = (productId: string, e: React.MouseEvent | React.TouchEvent): void => {
        if (userCartItems) {
            const newCart = userCartItems.filter((item: ICartItem) => item.productId !== productId);
            // Update locally
            setUserCartItems(newCart);
            // Update in server (debounced so rapid cart edits coalesce cleanly)
            queueCartSync(newCart);
            if (currentSessionId) {
                let type: STORE_ITEM_TYPE | undefined;
                switch (productId) {
                case WEB_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBook;
                    break;
                case WEB_BOOK_DAY_PASS_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookDayPass;
                    break;
                case WEB_BOOK_WEEK_PASS_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookWeekPass;
                    break;
                case WEB_BOOK_CHAPTER_ONE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterOne;
                    break;
                case WEB_BOOK_CHAPTER_TWO_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTwo;
                    break;
                case WEB_BOOK_CHAPTER_THREE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterThree;
                    break;
                case WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFour;
                    break;
                case WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFive;
                    break;
                case WEB_BOOK_CHAPTER_SIX_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSix;
                    break;
                case WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSeven;
                    break;
                case WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEight;
                    break;
                case WEB_BOOK_CHAPTER_NINE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterNine;
                    break;
                case WEB_BOOK_CHAPTER_TEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTen;
                    break;
                case WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEleven;
                    break;
                case WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterTwelve;
                    break;
                case WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterThirteen;
                    break;
                case WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFourteen;
                    break;
                case WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterFifteen;
                    break;
                case WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSixteen;
                    break;
                case WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterSeventeen;
                    break;
                case WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterEighteen;
                    break;
                case WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookChapterNineteen;
                    break;
                case WEB_BOOK_APPENDIX_A_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.webBookAppendixA;
                    break;
                case DIGITAL_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.digitalBook;
                    break;
                case DIGITAL_BOOK_PREORDER_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.digitalBookPreorder;
                    break;
                case AUDIO_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.audioBook;
                    break;
                case PHYSICAL_BOOK_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.physicalBook;
                    break;
                case PHYSICAL_BOOK_PREORDER_PRODUCT_ID:
                    type = STORE_ITEM_TYPE.physicalBookPreorder;
                    break;
                default:
                    // Shouldn't reach here
                }
                // Record user action
                if (type) {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.removeItemFromCart,
                        sessionId: currentSessionId,
                        payload: {
                            type,
                        },
                    });
                } else {
                    recordUserAction({
                        test: useStripeTestMode,
                        type: USER_ACTION_TYPE.removeItemFromCart,
                        sessionId: currentSessionId,
                    });
                }
            }
            // Play Sound
            if (hasSound && deleteClip.current) {
                deleteClip.current.pause();
                deleteClip.current.currentTime = 0;
                playAudio(deleteClip.current);
            }
        } else {
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_REMOVE_CART_ITEM_ERROR,
                icon: PixelCartIcon,
                hasFailure: true,
            });
        }
        onCursorLeave(e);
    };

    const onCheckoutButtonEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.checkoutActionButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onCheckoutButtonLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onCheckoutPolicyLinkEnter = (
        target: CURSOR_TARGET,
        e: React.MouseEvent | React.TouchEvent,
    ): void => {
        onCursorEnter(
            target,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onCheckoutPolicyLinkLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const isQuantityAdjustableProductId = (productId: string): boolean => (
        QUANTITY_ADJUSTABLE_PRODUCT_IDS.has(productId)
    );

    const onCheckoutButtonClick = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: useStripeTestMode,
                type: USER_ACTION_TYPE.viewCheckoutPage,
                sessionId: currentSessionId,
                payload: {
                    route: PAGE_ROUTE.cart,
                },
            });
        }
        navigate(
            `/${PAGE_ROUTE.checkout}/${CHECKOUT_CHECKPOINT.info}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    // ===== Side Effects =====

    /**
     * Manages page title changes
     */
    useEffect(() => {
        updatePageTitle(
            'Cart',
        );
    }, []);

    /**
     * Records Cart Page User Action
     */
    useEffect(() => {
        if (
            !currentSessionId
            || recordedViewPageUserActionKey.current === location.key
        ) return;
        recordCartPageUserAction();
        recordedViewPageUserActionKey.current = location.key;
    }, [
        currentSessionId,
        location.key,
    ]);

    /**
     * Fetches and saves store items
     */
    useEffect(() => {
        if (userCartItems && userCartItems.length > 0) fetchStoreItems();
    }, [userCartItems]);

    // ===== Memoization =====

    const WEB_BOOK_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_DAY_PASS_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_DAY_PASS_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_DAY_PASS_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_WEEK_PASS_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_WEEK_PASS_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_WEEK_PASS_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_ONE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_ONE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_TWO_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_TWO_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_THREE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_THREE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_FOUR_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_FOUR_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_FIVE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_FIVE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_SIX_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_SIX_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_SEVEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_SEVEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_EIGHT_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_EIGHT_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_NINE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_NINE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_TEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_TEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_ELEVEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_ELEVEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_TWELVE_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_TWELVE_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_THIRTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_THIRTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_FOURTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_FOURTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_FIFTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_FIFTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_SIXTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_SIXTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_SEVENTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_SEVENTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_EIGHTEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_EIGHTEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_CHAPTER_NINETEEN_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_CHAPTER_NINETEEN_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const WEB_BOOK_APPENDIX_A_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_TEST_API_ID
            : WEB_BOOK_APPENDIX_A_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const DIGITAL_BOOK_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? DIGITAL_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : DIGITAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const DIGITAL_BOOK_PREORDER_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID
            : DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const AUDIO_BOOK_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? AUDIO_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : AUDIO_BOOK_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const PHYSICAL_BOOK_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? PHYSICAL_BOOK_STRIPE_PRODUCT_TEST_API_ID
            : PHYSICAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const PHYSICAL_BOOK_PREORDER_PRODUCT_ID = useMemo(
        () => (useStripeTestMode
            ? PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID
            : PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID),
        [useStripeTestMode],
    );
    const QUANTITY_ADJUSTABLE_PRODUCT_IDS = useMemo(() => new Set([
        WEB_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
        WEB_BOOK_STRIPE_PRODUCT_TEST_API_ID,
        DIGITAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
        DIGITAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
        DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
        DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
        AUDIO_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
        AUDIO_BOOK_STRIPE_PRODUCT_TEST_API_ID,
        PHYSICAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
        PHYSICAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
        PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
        PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    ]), []);
    const COMPLIMENTARY_WEB_BOOK_QUALIFYING_PRODUCT_IDS = useMemo(() => new Set([
        DIGITAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
        DIGITAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
        DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
        DIGITAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
        PHYSICAL_BOOK_STRIPE_PRODUCT_LIVE_API_ID,
        PHYSICAL_BOOK_STRIPE_PRODUCT_TEST_API_ID,
        PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_LIVE_API_ID,
        PHYSICAL_BOOK_PREORDER_STRIPE_PRODUCT_TEST_API_ID,
    ]), []);
    const complimentaryWebBookQuantity = useMemo(() => {
        if (
            !userCartItems
            || userCartItems.length === 0
        ) return 0;

        return userCartItems.reduce((total: number, item: ICartItem) => (
            COMPLIMENTARY_WEB_BOOK_QUALIFYING_PRODUCT_IDS.has(item.productId)
                ? total + item.quantity
                : total
        ), 0);
    }, [
        userCartItems,
        COMPLIMENTARY_WEB_BOOK_QUALIFYING_PRODUCT_IDS,
    ]);
    const cartBookInviteNote = useMemo(() => getCartBookInviteNote(userCartItems), [userCartItems]);

    const numCartItems = useMemo(() => {
        let count = 0;
        if (
            userCartItems
            && userCartItems.length > 0
        ) {
            userCartItems.forEach((item: ICartItem) => {
                count += item.quantity;
            });
            // Add complimentary web-book quantity matching qualifying items.
            count += complimentaryWebBookQuantity;
        }
        return count;
    }, [
        userCartItems,
        complimentaryWebBookQuantity,
    ]);

    const cartHasPhysicalItem = useMemo(() => {
        if (userCartItems) {
            return userCartItems.reduce((hasPhysical, item) => {
                const storeItem = storeItemsByPrice.get(item.priceId);
                if (storeItem && storeItem?.physical) return true;
                return hasPhysical;
            }, false);
        }
        return false;
    }, [
        userCartItems,
        storeItemsByPrice,
    ]);

    const subtotal = useMemo(() => {
        if (
            user
            && userCartItems
            && userCartItems.length > 0
            && fetchedStoreItems
        ) {
            return userCartItems.reduce((
                total: number,
                item: ICartItem,
            ) => {
                const storeItem = storeItemsByPrice.get(item.priceId);
                if (
                    storeItem
                    && storeItem.amount
                ) {
                    return total + (item.quantity * storeItem.amount);
                }

                return total;
            }, 0);
        }
    }, [
        userCartItems,
        fetchedStoreItems,
    ]);

    return (
        <Container>
            <PageLogo
                dark
                withEnterTransition
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e) => onLogoEnter(e),
                    onMouseLeave: (e) => onLogoLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onLogoClick })}
            >
                <ReactSVG
                    src={VerascopeLogo}
                />
            </PageLogo>
            <PageTitle
                withEnterTransition
                color={theme.verascopeColor.purple200}
            >
                CART
            </PageTitle>
            <PageSubtitle
                withEnterTransition
                color={theme.verascopeColor.purple400}
                hideOnExtraSmall
            >
                Your haul.
            </PageSubtitle>
            <ContentContainer>
                {userCartItems && userCartItems.length > 0
                    ? (
                        <PopulatedCart>
                            <CartItemsContainer>
                                <Header>
                                    <HeaderText>
                                        {fetchedStoreItems ? (
                                            <>
                                                {`Item${numCartItems !== 1 ? 's' : ''}`}
                                                {userCartItems && (
                                                    <CartItemsCountText>
                                                        {numCartItems}
                                                    </CartItemsCountText>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                Items
                                            </>
                                        )}
                                    </HeaderText>
                                    {fetchedStoreItems && (
                                        <EmptyCartButtonContainer>
                                            <EmptyCartButton
                                                className={HOVER_TARGET_CLASSNAME}
                                                customCursorActive={customCursorActive}
                                                detectTouchDevice={detectTouchDevice(document)}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: (e) => onEmptyCartButtonEnter(e),
                                                    onMouseLeave: (e) => onEmptyCartButtonLeave(e),
                                                })}
                                                {...getPressEventHandlers({ onMouseDown: onEmptyCartButtonClick })}
                                            >
                                                Empty
                                            </EmptyCartButton>
                                        </EmptyCartButtonContainer>
                                    )}
                                </Header>
                                <CartItems>
                                    {fetchedStoreItems && userCartItems
                                        ? (
                                            userCartItems.map((cartItem: ICartItem) => {
                                                const storeItem = storeItemsByPrice.get(cartItem.priceId)!;
                                                const quantityAdjustable = isQuantityAdjustableProductId(cartItem.productId);
                                                const cartItemBookInviteNote = (
                                                    cartBookInviteNote?.productId === cartItem.productId
                                                        ? cartBookInviteNote.text
                                                        : null
                                                );
                                                return (
                                                    <CartItemContainer>
                                                        <CartItemImageContainer
                                                            hasImage={storeItem.imageURLs.length > 0}
                                                        >
                                                            {storeItem.imageURLs.length > 0
                                                                ? (
                                                                    <CartItemImage
                                                                        url={storeItem.imageURLs[0]}
                                                                    />
                                                                ) : (
                                                                    <ReactSVG
                                                                        src={BoxIcon}
                                                                    />
                                                                )}
                                                        </CartItemImageContainer>
                                                        <CartItemDetails>
                                                            <CartItemName>
                                                                {storeItem.name}
                                                            </CartItemName>
                                                            <CartItemPrice>
                                                                {`$${roundToNDecimals(storeItem.amount! / CENTS_IN_A_DOLLAR, 2)}`}
                                                            </CartItemPrice>
                                                            <CartItemQuantityContainer>
                                                                <CartItemQuantityText>
                                                                    Quantity
                                                                </CartItemQuantityText>
                                                                {quantityAdjustable && (
                                                                    <CartItemQuantityButton
                                                                        isDisabled={false}
                                                                        data-testid={`cart-decrement-${cartItem.productId}`}
                                                                        className={HOVER_TARGET_CLASSNAME}
                                                                        customCursorActive={customCursorActive}
                                                                        detectTouchDevice={detectTouchDevice(document)}
                                                                        {...getTouchMouseEventHandlers({
                                                                            onMouseEnter: (e) => onCartItemQuantityButtonEnter(e),
                                                                            onMouseLeave: (e) => onCartItemQuantityButtonLeave(e),
                                                                        })}
                                                                        {...getPressEventHandlers({ onMouseDown: () => onCartItemDecrementQuantityButtonClick(cartItem.productId) })}
                                                                    >
                                                                        <ReactSVG
                                                                            src={PixelMinusIcon}
                                                                        />
                                                                    </CartItemQuantityButton>
                                                                )}
                                                                <CartItemQuantityCount
                                                                    nonInteractable={!quantityAdjustable}
                                                                >
                                                                    {cartItem.quantity}
                                                                </CartItemQuantityCount>
                                                                {quantityAdjustable && (
                                                                    <CartItemQuantityButton
                                                                        isDisabled={false}
                                                                        data-testid={`cart-increment-${cartItem.productId}`}
                                                                        className={HOVER_TARGET_CLASSNAME}
                                                                        customCursorActive={customCursorActive}
                                                                        detectTouchDevice={detectTouchDevice(document)}
                                                                        {...getTouchMouseEventHandlers({
                                                                            onMouseEnter: (e) => onCartItemQuantityButtonEnter(e),
                                                                            onMouseLeave: (e) => onCartItemQuantityButtonLeave(e),
                                                                        })}
                                                                        {...getPressEventHandlers({ onMouseDown: () => onCartItemIncrementQuantityButtonClick(cartItem.productId) })}
                                                                    >
                                                                        <ReactSVG
                                                                            src={PixelPlusIcon}
                                                                        />
                                                                    </CartItemQuantityButton>
                                                                )}
                                                                <CartItemQuantityPriceContainer>
                                                                    {`$${roundToNDecimals(cartItem.quantity * (storeItem.amount! / CENTS_IN_A_DOLLAR), 2)}`}
                                                                </CartItemQuantityPriceContainer>
                                                            </CartItemQuantityContainer>
                                                            {cartItemBookInviteNote && (
                                                                <CartItemNote>
                                                                    {cartItemBookInviteNote}
                                                                </CartItemNote>
                                                            )}
                                                            <CartItemRemoveButton
                                                                data-testid={`cart-remove-${cartItem.productId}`}
                                                                className={HOVER_TARGET_CLASSNAME}
                                                                customCursorActive={customCursorActive}
                                                                detectTouchDevice={detectTouchDevice(document)}
                                                                {...getTouchMouseEventHandlers({
                                                                    onMouseEnter: (e) => onCartItemRemoveButtonEnter(e),
                                                                    onMouseLeave: (e) => onCartItemRemoveButtonLeave(e),
                                                                })}
                                                                {...getPressEventHandlers({ onMouseDown: (e) => onCartItemRemoveButtonClick(cartItem.productId, e) })}
                                                            >
                                                                <ReactSVG
                                                                    src={PixelCrossIcon}
                                                                />
                                                            </CartItemRemoveButton>
                                                        </CartItemDetails>
                                                    </CartItemContainer>
                                                );
                                            })
                                        )
                                        : (
                                            <>
                                                <CartItemContainer>
                                                    <CartItemImageContainer
                                                        hasImage={false}
                                                    >
                                                        <ReactSVG
                                                            src={BoxIcon}
                                                        />
                                                    </CartItemImageContainer>
                                                    <CartItemDetails>
                                                        <CartItemName>
                                                            <PlaceholderBox
                                                                width={CART_ITEM_NAME_PLACEHOLDER_WIDTH}
                                                                height={CART_ITEM_NAME_PLACEHOLDER_HEIGHT}
                                                            />
                                                        </CartItemName>
                                                        <CartItemPrice>
                                                            <PlaceholderBox
                                                                width={CART_ITEM_PRICE_PLACEHOLDER_WIDTH}
                                                                height={CART_ITEM_PRICE_PLACEHOLDER_HEIGHT}
                                                            />
                                                        </CartItemPrice>
                                                    </CartItemDetails>
                                                </CartItemContainer>
                                                <CartItemContainer>
                                                    <CartItemImageContainer
                                                        hasImage={false}
                                                    >
                                                        <ReactSVG
                                                            src={BoxIcon}
                                                        />
                                                    </CartItemImageContainer>
                                                    <CartItemDetails>
                                                        <CartItemName>
                                                            <PlaceholderBox
                                                                width={CART_ITEM_NAME_PLACEHOLDER_WIDTH}
                                                                height={CART_ITEM_NAME_PLACEHOLDER_HEIGHT}
                                                            />
                                                        </CartItemName>
                                                        <CartItemPrice>
                                                            <PlaceholderBox
                                                                width={CART_ITEM_PRICE_PLACEHOLDER_WIDTH}
                                                                height={CART_ITEM_PRICE_PLACEHOLDER_HEIGHT}
                                                            />
                                                        </CartItemPrice>
                                                    </CartItemDetails>
                                                </CartItemContainer>
                                                <CartItemContainer>
                                                    <CartItemImageContainer
                                                        hasImage={false}
                                                    >
                                                        <ReactSVG
                                                            src={BoxIcon}
                                                        />
                                                    </CartItemImageContainer>
                                                    <CartItemDetails>
                                                        <CartItemName>
                                                            <PlaceholderBox
                                                                width={CART_ITEM_NAME_PLACEHOLDER_WIDTH}
                                                                height={CART_ITEM_NAME_PLACEHOLDER_HEIGHT}
                                                            />
                                                        </CartItemName>
                                                        <CartItemPrice>
                                                            <PlaceholderBox
                                                                width={CART_ITEM_PRICE_PLACEHOLDER_WIDTH}
                                                                height={CART_ITEM_PRICE_PLACEHOLDER_HEIGHT}
                                                            />
                                                        </CartItemPrice>
                                                    </CartItemDetails>
                                                </CartItemContainer>
                                            </>
                                        )}
                                    {complimentaryWebBookQuantity > 0
                                    && webBookStoreItem
                                    && (
                                        <CartItemContainer>
                                            <CartItemImageContainer
                                                hasImage={webBookStoreItem.imageURLs.length > 0}
                                            >
                                                {webBookStoreItem.imageURLs.length > 0
                                                    ? (
                                                        <CartItemImage
                                                            url={webBookStoreItem.imageURLs[0]}
                                                        />
                                                    ) : (
                                                        <ReactSVG
                                                            src={BoxIcon}
                                                        />
                                                    )}
                                            </CartItemImageContainer>
                                            <CartItemDetails>
                                                <CartItemName>
                                                    {`${webBookStoreItem.name} (Complimentary)`}
                                                </CartItemName>
                                                <CartItemPrice>
                                                    <DiscountedCartItemPrice>
                                                        {`$${roundToNDecimals(
                                                            (webBookStoreItem.amount! * complimentaryWebBookQuantity) / CENTS_IN_A_DOLLAR,
                                                            2,
                                                        )}`}
                                                    </DiscountedCartItemPrice>
                                                    <CartItemPriceAfterDiscount>
                                                        {' $0.00'}
                                                    </CartItemPriceAfterDiscount>
                                                </CartItemPrice>
                                                <CartItemQuantityContainer>
                                                    <CartItemQuantityText>
                                                        Quantity
                                                    </CartItemQuantityText>
                                                    <CartItemQuantityCount
                                                        nonInteractable
                                                    >
                                                        {complimentaryWebBookQuantity}
                                                    </CartItemQuantityCount>
                                                    <CartItemQuantityPriceContainer>
                                                        {' $0.00'}
                                                    </CartItemQuantityPriceContainer>
                                                </CartItemQuantityContainer>
                                            </CartItemDetails>
                                        </CartItemContainer>
                                    )}
                                </CartItems>
                            </CartItemsContainer>
                            <OrderSummaryContainer>
                                <Header>
                                    <HeaderText>
                                        Order Summary
                                    </HeaderText>
                                </Header>
                                <OrderSummary>
                                    <OrderSummaryEntries>
                                        <OrderSummaryEntry>
                                            <OrderSummaryText>
                                                Cost
                                            </OrderSummaryText>
                                            <OrderSummaryPrice>
                                                {fetchedStoreItems
                                                && subtotal != null
                                                    ? `$${roundToNDecimals(subtotal / CENTS_IN_A_DOLLAR, 2)}`
                                                    : (
                                                        <PlaceholderBox
                                                            width={ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH}
                                                            height={ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT}
                                                        />
                                                    )}
                                            </OrderSummaryPrice>
                                        </OrderSummaryEntry>
                                        {cartHasPhysicalItem && (
                                            <OrderSummaryEntry>
                                                <OrderSummaryText>
                                                    Shipping
                                                </OrderSummaryText>
                                                <OrderSummaryPrice>
                                                    {fetchedStoreItems
                                                        ? 'TBD'
                                                        : (
                                                            <PlaceholderBox
                                                                width={ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH}
                                                                height={ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT}
                                                            />
                                                        )}
                                                </OrderSummaryPrice>
                                            </OrderSummaryEntry>
                                        )}
                                        <OrderSummaryEntry
                                            divider
                                        >
                                            <OrderSummaryText>
                                                Subtotal
                                            </OrderSummaryText>
                                            <OrderSummaryPrice>
                                                {fetchedStoreItems
                                                && subtotal != null
                                                    ? `$${roundToNDecimals(subtotal / CENTS_IN_A_DOLLAR, 2)}`
                                                    : (
                                                        <PlaceholderBox
                                                            width={ORDER_SUMMARY_PRICE_PLACEHOLDER_WIDTH}
                                                            height={ORDER_SUMMARY_PRICE_PLACEHOLDER_HEIGHT}
                                                        />
                                                    )}
                                            </OrderSummaryPrice>
                                        </OrderSummaryEntry>
                                        <CheckoutButton
                                            data-testid="cart-checkout-button"
                                            className={HOVER_TARGET_CLASSNAME}
                                            customCursorActive={customCursorActive}
                                            detectTouchDevice={detectTouchDevice(document)}
                                            {...getTouchMouseEventHandlers({
                                                onMouseEnter: (e) => onCheckoutButtonEnter(e),
                                                onMouseLeave: (e) => onCheckoutButtonLeave(e),
                                            })}
                                            {...(fetchedStoreItems ? {
                                                ...getPressEventHandlers({ onMouseDown: onCheckoutButtonClick }),
                                            } : {})}
                                        >
                                            {!fetchedStoreItems
                                                ? (
                                                    <SpinnerContainer
                                                        src={Spinner}
                                                        alt="spinner"
                                                    />
                                                )
                                                : 'Checkout'}
                                        </CheckoutButton>
                                        <CheckoutTrustContainer>
                                            <CheckoutTrustText>
                                                Secure checkout powered by Stripe.
                                            </CheckoutTrustText>
                                            <CheckoutTrustText>
                                                Your card details are encrypted and sent directly to Stripe. We never store your full card number.
                                            </CheckoutTrustText>
                                            <CheckoutTrustLinkRow>
                                                <CheckoutTrustLink
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    customCursorActive={customCursorActive}
                                                    href={`/${PAGE_ROUTE.privacyPolicy}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: (e) => onCheckoutPolicyLinkEnter(CURSOR_TARGET.privacyPolicyButton, e),
                                                        onMouseLeave: (e) => onCheckoutPolicyLinkLeave(e),
                                                    })}
                                                >
                                                    Privacy Policy
                                                </CheckoutTrustLink>
                                                {' • '}
                                                <CheckoutTrustLink
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    customCursorActive={customCursorActive}
                                                    href={`/${PAGE_ROUTE.termsOfUse}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: (e) => onCheckoutPolicyLinkEnter(CURSOR_TARGET.termsOfUseButton, e),
                                                        onMouseLeave: (e) => onCheckoutPolicyLinkLeave(e),
                                                    })}
                                                >
                                                    Refund Policy
                                                </CheckoutTrustLink>
                                                {' • '}
                                                <CheckoutTrustLink
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    customCursorActive={customCursorActive}
                                                    href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: (e) => onCheckoutPolicyLinkEnter(CURSOR_TARGET.linkButton, e),
                                                        onMouseLeave: (e) => onCheckoutPolicyLinkLeave(e),
                                                    })}
                                                >
                                                    Contact Support
                                                </CheckoutTrustLink>
                                            </CheckoutTrustLinkRow>
                                        </CheckoutTrustContainer>
                                    </OrderSummaryEntries>
                                </OrderSummary>
                            </OrderSummaryContainer>
                        </PopulatedCart>
                    ) : (
                        <EmptyCart>
                            <EmptyCartIcon>
                                <ReactSVG
                                    src={PixelCartIcon}
                                />
                            </EmptyCartIcon>
                            <EmptyCartTitle>
                                Your cart is empty.
                            </EmptyCartTitle>
                            <BuyBookButton
                                className={HOVER_TARGET_CLASSNAME}
                                customCursorActive={customCursorActive}
                                detectTouchDevice={detectTouchDevice(document)}
                                {...getTouchMouseEventHandlers({
                                    onMouseEnter: (e) => onBuyBookButtonEnter(e),
                                    onMouseLeave: (e) => onBuyBookButtonLeave(e),
                                })}
                                {...getPressEventHandlers({ onMouseDown: onBuyBookButtonClick })}
                            >
                                Buy Book
                            </BuyBookButton>
                        </EmptyCart>
                    )}
            </ContentContainer>
        </Container>
    );
}

export default CartView;
